import {
  HOUSE_ABOUT_STEP,
  HOUSE_FIND_STEP,
  HOUSE_PRICE_STEP,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House";
import {
  isFullRental,
  isPartialRental,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House/conditions";
import {
  HouseStandard,
  HouseType,
  RoofType,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House/houseTypes";
import {
  formBooleanToBoolean,
  nullableFormBooleanToNullableBoolean,
} from "Utils/formUtils";
import { hasDefinedProperties, isDefined } from "Utils/functions";

import {
  AdditionalBuildingFormValues,
  HousePriceFormValues,
  HouseProductDetailsFormValues,
  HouseProductInfoDetailsFormValues,
  HouseProductIntroFormValues,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/House/houseFormValuesInterfaces";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { mapFormToInjuries } from "Store/priceCalculation/productMappers/common/injuriesMapper";
import {
  mapBuildingIsolation,
  mapFromBuildingFormType,
} from "../ProductDetails/houseProductDetailsUtils";
import { HOUSE_BASIC_INFO_STEP } from "../houseSteps";

interface PostalAddress {
  line1?: string;
  city: string;
  zipCode: string;
  cadastralKnr?: number;
  cadastralGnr?: number;
  cadastralBnr?: number;
  cadastralFnr?: number;
  cadastralSnr?: number;
}

interface RealEstateInfo {
  grossArea?: number;
  livingFloorSpace?: number;
  constructionYear?: number;
  houseType?: HouseType;
  postNumber?: number;
  city?: string;
}

interface MappedAdditionalBuilding {
  constructionYear: string;
  coverage: string;
  grossArea: string;
  type: string;
  isIsolated?: boolean;
}

export type HouseProductBackend = {
  additionalBuildings: MappedAdditionalBuilding[] | null;
  address?: PostalAddress;
  addressSameAsHomeAddress?: boolean;
  buildingStandard: string;
  cadastralBnr?: string;
  cadastralGnr?: string;
  constructionYear: number;
  coverageGroup: any;
  crawlspace: boolean;
  damageCountRecentYears: number;
  fireAlarmCode?: boolean;
  grossArea: number;
  hasAdditionalBuildings?: boolean;
  hasBasementRoom?: boolean;
  hasBurglarAlarm: boolean;
  hasCircuitBreakers?: boolean;
  hasFireAlarm: boolean;
  hasFloodRisk?: boolean;
  hasPartialRental: boolean;
  hasPipeInPipeSystem?: boolean;
  hasRental: boolean;
  hasWaterGuard: boolean;
  houseType: HouseType;
  isRoofChanged?: boolean;
  isWetAreasRehabilitatedOrChanged?: boolean;
  isWithHistoryOfOfWaterOrMoistureDamage?: boolean;
  livingFloorSpace: number;
  maintenanceConfirmation: boolean;
  name: INSURANCE_NAME_VALUE;
  onlyFireAndNatureDamage?: boolean;
  previousFungusDamage?: boolean;
  realEstateInfo?: RealEstateInfo;
  recentlyBought?: boolean;
  roofType?: RoofType;
  streetAddress?: string;
  yearOfLastDamage?: number;
};

export function mapToCommerceHouse(data: {
  [key: string]: any;
}): HouseProductBackend {
  const findHouse: HouseProductIntroFormValues =
    data.formData[HOUSE_FIND_STEP.id];
  const basicInfo: HouseProductInfoDetailsFormValues =
    data.formData[HOUSE_BASIC_INFO_STEP.id];
  const aboutHouse: HouseProductDetailsFormValues =
    data.formData[HOUSE_ABOUT_STEP.id];
  const checkPrice: HousePriceFormValues = data.formData[HOUSE_PRICE_STEP.id];

  const mapAdditionalBuildings = basicInfo.otherBuildings.map(
    (building: AdditionalBuildingFormValues): MappedAdditionalBuilding => {
      return {
        constructionYear: building.constructionYear,
        coverage: "Standard",
        grossArea: String(building.areal),
        type: mapFromBuildingFormType(building.type),
        isIsolated: mapBuildingIsolation(building.type),
      };
    },
  );

  return {
    name: INSURANCE_NAME_VALUE.HOUSE,
    coverageGroup: data.coverage,
    additionalBuildings:
      mapAdditionalBuildings.length > 0 ? mapAdditionalBuildings : null,
    address: getPostalAddress(findHouse),
    recentlyBought: nullableFormBooleanToNullableBoolean(
      findHouse.recentlyBought,
    ),
    buildingStandard: HouseStandard.NORMAL,
    constructionYear: Number(basicInfo.constructionYear),
    crawlspace: false, // TODO - Forventes av backend
    grossArea: basicInfo.bta,
    hasAdditionalBuildings: mapAdditionalBuildings.length > 0,
    hasBurglarAlarm: aboutHouse.hasBurglarAlarm,
    hasFireAlarm: aboutHouse.hasFireAlarm,
    hasWaterGuard: aboutHouse.hasWaterGuard,
    hasCircuitBreakers: aboutHouse.hasCircuitBreakers,
    hasPipeInPipeSystem: aboutHouse.hasPipeInPipeSystem,
    onlyFireAndNatureDamage: checkPrice.onlyFireAndNatureDamage,
    hasBasementRoom:
      nullableFormBooleanToNullableBoolean(aboutHouse.hasBasementRoom) ??
      findHouse.hasBasementRoom,
    hasFloodRisk: findHouse.hasFloodRisk,
    hasPartialRental: isPartialRental(aboutHouse.rentalOrUnoccupied),
    hasRental: isFullRental(aboutHouse.rentalOrUnoccupied),
    addressSameAsHomeAddress: formBooleanToBoolean(
      findHouse.addressSameAsHomeAddress,
    ),
    houseType: basicInfo.houseType,
    roofType: findHouse.roofType,
    livingFloorSpace: basicInfo.boa,
    previousFungusDamage: nullableFormBooleanToNullableBoolean(
      aboutHouse.pestsDamaged,
    ),
    ...mapFormToInjuries(aboutHouse.injuries, aboutHouse.injuryYear),
    isRoofChanged: nullableFormBooleanToNullableBoolean(
      aboutHouse.isRoofChanged,
    ),
    isWithHistoryOfOfWaterOrMoistureDamage:
      nullableFormBooleanToNullableBoolean(
        aboutHouse.isWithHistoryOfWaterOrMoistureDamage,
      ),
    isWetAreasRehabilitatedOrChanged: nullableFormBooleanToNullableBoolean(
      aboutHouse.isWetAreasRehabilitatedOrChanged,
    ),
    realEstateInfo: findHouse.realEstateInfo,
    maintenanceConfirmation: formBooleanToBoolean(
      aboutHouse.maintenanceConfirmation,
    ),
  };
}

export const getPostalAddress = (
  findHouse: HouseProductIntroFormValues,
): PostalAddress | undefined => {
  if (
    hasDefinedProperties(findHouse, [
      "address",
      "postNumber",
      "city",
      "cadastralGnr",
      "cadastralBnr",
      "realEstateInfo",
    ])
  ) {
    return {
      line1: findHouse.address,
      zipCode: findHouse.postNumber,
      city: findHouse.city,
      cadastralKnr: findHouse.cadastralKnr,
      cadastralGnr: findHouse.cadastralGnr,
      cadastralBnr: findHouse.cadastralBnr,
      cadastralFnr: findHouse.cadastralFnr,
      cadastralSnr: findHouse.cadastralSnr,
    };
  }
  if (isDefined(findHouse.city) && isDefined(findHouse.postNumber)) {
    return {
      city: findHouse.city,
      zipCode: findHouse.postNumber,
    };
  }
  return undefined;
};

export const TEST_POSTAL_ADDRESS_VALUES: Pick<
  PostalAddress,
  | "line1"
  | "cadastralKnr"
  | "cadastralGnr"
  | "cadastralBnr"
  | "cadastralFnr"
  | "cadastralSnr"
> = {
  line1: "Test address 1A",
  cadastralKnr: 100,
  cadastralGnr: 1,
  cadastralBnr: 2,
  cadastralFnr: 0,
  cadastralSnr: 0,
};
